import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../AppLayout';
import axios from 'axios';

const API_BASE_URL = '/api/v1';

const PageContainer = styled.div`
  padding: 40px 20px 80px;
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const PageTitle = styled(motion.h1)`
  font-family: 'Orbitron', sans-serif;
  font-size: 36px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 15px;
`;

const PageDesc = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

const FilterBtn = styled(motion.button)`
  padding: 10px 25px;
  background: ${props => props.$active ? 
    'linear-gradient(135deg, var(--primary), var(--secondary))' : 
    'transparent'};
  border: 1px solid ${props => props.$active ? 'var(--primary)' : 'var(--border-color)'};
  border-radius: 25px;
  color: ${props => props.$active ? '#fff' : 'var(--text-secondary)'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
`;

const GalleryCard = styled(motion.div)`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary);
    transform: translateY(-5px);
    box-shadow: 0 10px 40px rgba(0, 240, 255, 0.2);
  }
`;

const CardThumbnail = styled.div`
  width: 100%;
  padding-top: 177.78%; /* 9:16 aspect ratio */
  background: linear-gradient(135deg, var(--bg-dark), var(--bg-card));
  position: relative;
  overflow: hidden;
`;

const ThumbnailPreview = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 60px;
  background: ${props => props.$gradient || 'linear-gradient(135deg, var(--primary), var(--secondary))'};
`;

const PlayIcon = styled(motion.div)`
  position: absolute;
  width: 60px;
  height: 60px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  
  &:hover {
    background: var(--primary);
  }
`;

const CardContent = styled.div`
  padding: 20px;
`;

const CardTitle = styled.h4`
  font-family: 'Orbitron', sans-serif;
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 10px;
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: var(--text-secondary);
`;

const CardTags = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Tag = styled.span`
  padding: 4px 12px;
  background: rgba(0, 240, 255, 0.1);
  border-radius: 15px;
  font-size: 12px;
  color: var(--primary);
`;

const CardActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
`;

const ActionBtn = styled(motion.button)`
  flex: 1;
  padding: 10px;
  background: ${props => props.$primary ? 
    'linear-gradient(135deg, var(--primary), var(--secondary))' : 
    'transparent'};
  border: 1px solid ${props => props.$primary ? 'none' : 'var(--border-color)'};
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 80px;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const mockWorks = [
  {
    id: 1,
    title: '产品介绍视频',
    type: '视频',
    duration: '01:30',
    date: '2024-01-15',
    avatar: '👩‍💼',
    gradient: 'linear-gradient(135deg, #667eea, #764ba2)',
    tags: ['商业', '产品']
  },
  {
    id: 2,
    title: '教育培训课程',
    type: '视频',
    duration: '05:20',
    date: '2024-01-14',
    avatar: '👨‍🏫',
    gradient: 'linear-gradient(135deg, #f093fb, #f5576c)',
    tags: ['教育', '培训']
  },
  {
    id: 3,
    title: '新闻播报',
    type: '视频',
    duration: '02:15',
    date: '2024-01-13',
    avatar: '📺',
    gradient: 'linear-gradient(135deg, #4facfe, #00f2fe)',
    tags: ['新闻', '播报']
  },
  {
    id: 4,
    title: '智能语音播客',
    type: '音频',
    duration: '03:45',
    date: '2024-01-12',
    avatar: '🎙️',
    gradient: 'linear-gradient(135deg, #43e97b, #38f9d7)',
    tags: ['播客', '语音']
  },
  {
    id: 5,
    title: '品牌宣传片',
    type: '视频',
    duration: '01:00',
    date: '2024-01-11',
    avatar: '🏢',
    gradient: 'linear-gradient(135deg, #fa709a, #fee140)',
    tags: ['品牌', '宣传']
  },
  {
    id: 6,
    title: '多语言客服',
    type: '视频',
    duration: '00:45',
    date: '2024-01-10',
    avatar: '🌍',
    gradient: 'linear-gradient(135deg, #a8edea, #fed6e3)',
    tags: ['客服', '多语言']
  },
];

const filters = ['全部', '视频', '音频', '最近7天', '最近30天'];

const GalleryPage = () => {
  const [activeFilter, setActiveFilter] = useState('全部');
  const [selectedWork, setSelectedWork] = useState(null);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorks();
  }, []);

  const fetchWorks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/user/video/tasks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('[DEBUG] 作品库API响应:', response.data);
      
      if (response.data.code === 1 && response.data.data) {
        const tasks = Array.isArray(response.data.data) ? response.data.data : [];
        
        const completedWorks = tasks
          .filter(task => task.status === 2)
          .map(task => ({
            id: task.id,
            title: task.task_name || '未命名视频',
            type: '视频',
            duration: task.duration || '0秒',
            date: task.created_at || '',
            tags: [task.platform_code || 'Pro'],
            video_url: task.video_url || '',
            thumbnail: task.thumbnail || (task.video_url ? `${task.video_url.split('?')[0]}?x-oss-process=video/snapshot,t_0,f_jpg,m_fast` : ''),
            avatar: '🎬',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }));
        
        setWorks(completedWorks);
      }
    } catch (error) {
      console.error('获取作品列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredWorks = activeFilter === '全部' 
    ? works 
    : works.filter(work => work.type === activeFilter);

  return (
    <Layout>
      {/* 视频播放器模态框 */}
      {selectedWork && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(10px)'
        }}
        onClick={() => setSelectedWork(null)}
        >
          <div style={{
            width: '90%',
            maxWidth: '900px',
            background: 'var(--bg-dark)',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ 
                color: 'var(--text-primary)', 
                margin: 0,
                fontSize: '18px'
              }}>
                🎬 {selectedWork.title}
              </h3>
              <button 
                onClick={() => setSelectedWork(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '5px',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--danger)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
              >
                ✕
              </button>
            </div>
            
            <div style={{
              padding: '20px',
              textAlign: 'center'
            }}>
              {selectedWork.video_url ? (
                <div style={{
                  width: '100%',
                  maxWidth: '400px',
                  margin: '0 auto',
                  aspectRatio: '9/16'
                }}>
                  <video
                    controls
                    autoPlay
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      outline: 'none'
                    }}
                  >
                    <source src={selectedWork.video_url} type="video/mp4" />
                    您的浏览器不支持视频播放
                  </video>
                </div>
              ) : (
                <div style={{
                  padding: '60px',
                  color: 'var(--text-secondary)',
                  fontSize: '16px'
                }}>
                  视频链接无效
                </div>
              )}
            </div>
            
            <div style={{
              padding: '15px 20px',
              borderTop: '1px solid var(--border-color)',
              textAlign: 'right'
            }}>
              <button
                onClick={() => {
                  if (selectedWork.video_url) {
                    const link = document.createElement('a');
                    link.href = selectedWork.video_url;
                    link.download = `${selectedWork.title}.mp4`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}
                style={{
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                下载视频
              </button>
            </div>
          </div>
        </div>
      )}

      <PageContainer>
        <PageHeader>
          <PageTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            作品库
          </PageTitle>
          <PageDesc>查看和管理您创作的所有数字人内容</PageDesc>
        </PageHeader>

        <FilterBar>
          {filters.map((filter) => (
            <FilterBtn
              key={filter}
              $active={activeFilter === filter}
              onClick={() => setActiveFilter(filter)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {filter}
            </FilterBtn>
          ))}
        </FilterBar>

        <AnimatePresence mode='wait'>
          {filteredWorks.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📁</EmptyIcon>
              <h3 style={{ marginBottom: '10px', color: 'var(--text-primary)' }}>暂无作品</h3>
              <p>开始创作您的第一个数字人内容吧</p>
            </EmptyState>
          ) : (
            <GalleryGrid>
              {filteredWorks.map((work, index) => (
                <GalleryCard
                  key={work.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <CardThumbnail>
                    {work.thumbnail ? (
                      <img 
                        src={work.thumbnail} 
                        alt={work.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          cursor: 'pointer'
                        }}
                        onClick={() => setSelectedWork(work)}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          if (e.target.parentElement) {
                            const fallbackDiv = document.createElement('div');
                            fallbackDiv.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 60px; background: linear-gradient(135deg, var(--primary), var(--secondary)); cursor: pointer;';
                            fallbackDiv.textContent = work.avatar;
                            fallbackDiv.onclick = () => setSelectedWork(work);
                            e.target.parentElement.appendChild(fallbackDiv);
                          }
                        }}
                      />
                    ) : (
                      <ThumbnailPreview $gradient={work.gradient} style={{ cursor: 'pointer' }} onClick={() => setSelectedWork(work)}>
                        {work.avatar}
                      </ThumbnailPreview>
                    )}
                    {work.type === '视频' && (
                      <PlayIcon
                        whileHover={{ scale: 1.1 }}
                        onClick={() => setSelectedWork(work)}
                      >
                        ▶
                      </PlayIcon>
                    )}
                  </CardThumbnail>
                  
                  <CardContent>
                    <CardTitle>{work.title}</CardTitle>
                    <CardMeta>
                      <span>{work.duration}</span>
                      <span>{work.date}</span>
                    </CardMeta>
                    <CardTags>
                      {work.tags.map((tag) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </CardTags>
                    <CardActions>
                      <ActionBtn
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedWork(work)}
                      >
                        预览
                      </ActionBtn>
                      <ActionBtn 
                        $primary
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          if (work.video_url) {
                            const link = document.createElement('a');
                            link.href = work.video_url;
                            link.download = `${work.title}.mp4`;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }}
                      >
                        下载
                      </ActionBtn>
                    </CardActions>
                  </CardContent>
                </GalleryCard>
              ))}
            </GalleryGrid>
          )}
        </AnimatePresence>
      </PageContainer>
    </Layout>
  );
};

export default GalleryPage;
